import React from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { linkedin, google } from 'assets/icons';
import { useNavigate } from 'react-router-dom';
import { success } from 'assets/images';
import Swal from 'sweetalert2';

function SignUpForm() {
	const navigate = useNavigate();

	// Formik configuration
	const formik = useFormik({
		initialValues: {
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			checkbox: false,
		},
		validationSchema: Yup.object({
			firstName: Yup.string()
				.min(3, 'Must be at least 3 characters')
				.required('Required'),
			lastName: Yup.string()
				.min(3, 'Must be at least 3 characters')
				.required('Required'),
			email: Yup.string().email('Invalid email').required('Required'),
			password: Yup.string()
				.min(6, 'Password must be at least 6 characters')
				.max(24, 'Password can be maximum 24 characters')
				.required('Required'),
			checkbox: Yup.bool()
				.required('Required')
				.oneOf([true], 'Accept the terms and conditions'),
		}),
		onSubmit: async (values) => {
			try {
				const response = await fetch(
					'http://localhost:5000/api/auth/register',
					{
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(values),
					}
				);
				if (response.ok) {
					Swal.fire({
						imageUrl: success,
						imageWidth: 100,
						imageHeight: 100,
						imageAlt: 'Success',
						title: 'Submitted Successfully',
						text: 'You have successfully Signed Up.',
						confirmButtonColor: '#800080',
						backdrop: 'rgba(255,255,255,0.3)',
					});
					navigate('/sign-in');
				} else {
					throw new Error();
				}
			} catch {
				Swal.fire({
					title: 'Submission Unsuccessful',
					text: 'Kindly try again.',
					confirmButtonColor: '#800080',
				});
			}
		},
	});

	// Social Login Handlers
	const handleGoogleSignIn = () => {
		window.open('http://localhost:5000/api/auth/google', '_self');
	};

	const handleLinkedInSignIn = () => {
		window.open('http://localhost:5000/api/auth/linkedin', '_self');
	};

	return (
		<form onSubmit={formik.handleSubmit} className='lg:p-10 xl:p-10 2xl:p-10'>
			<h1 className='text-transparent font-semibold bg-clip-text bg-gradient-to-r from-purple to-gold w-80 py-8 text-2xl'>
				Create a free account
			</h1>
			<div className='flex flex-col md:flex-row lg:flex-row xl:flex-row 2xl:flex-row justify-between gap-x-4 gap-y-4'>
				<div
					className='border p-2 rounded md:w-1/2 lg:w-1/2 xl:w-1/2 2xl:w-1/2 text-white flex justify-center items-center cursor-pointer'
					onClick={handleGoogleSignIn}
				>
					<img src={google} width={20} alt='Google' />
					<p className='text-sm ml-2'>Continue with Google</p>
				</div>
				<div
					className='border p-2 rounded md:w-1/2 lg:w-1/2 xl:w-1/2 2xl:w-1/2 text-white flex justify-center items-center cursor-pointer'
					onClick={handleLinkedInSignIn}
				>
					<img src={linkedin} width={20} alt='LinkedIn' />
					<p className='text-sm ml-2'>Continue with LinkedIn</p>
				</div>
			</div>
			<div className='text-center flex pt-10 justify-between items-center pb-4'>
				<div className='border-b border-lilac w-80'></div>
				<span className='px-2 text-center text-white text-sm'>
					Or continue with
				</span>
				<div className='border-b border-lilac w-80'></div>
			</div>
			<div className='grid grid-cols-1 pt-4'>
				<label htmlFor='firstName' className='text-white text-base'>
					First Name <span className='text-red text-base'>*</span>
				</label>
				<input
					name='firstName'
					type='text'
					className='p-1 rounded w-full text-sm'
					placeholder='Enter your First Name'
					onChange={formik.handleChange}
					value={formik.values.firstName}
				/>
				{formik.touched.firstName && formik.errors.firstName && (
					<div className='text-red text-base'>{formik.errors.firstName}</div>
				)}
			</div>
			<div className='grid grid-cols-1 pt-4'>
				<label htmlFor='lastName' className='text-white text-base'>
					Last Name <span className='text-red text-base'>*</span>
				</label>
				<input
					name='lastName'
					type='text'
					className='p-1 rounded text-sm'
					placeholder='Enter your Last Name'
					onChange={formik.handleChange}
					value={formik.values.lastName}
				/>
				{formik.touched.lastName && formik.errors.lastName && (
					<div className='text-red text-base'>{formik.errors.lastName}</div>
				)}
			</div>
			<div className='grid grid-cols-1 pt-4'>
				<label htmlFor='email' className='text-white text-base'>
					Email Address <span className='text-red text-base'>*</span>
				</label>
				<input
					name='email'
					type='email'
					className='p-1 rounded text-sm'
					placeholder='Enter your Email Address'
					onChange={formik.handleChange}
					value={formik.values.email}
				/>
				{formik.touched.email && formik.errors.email && (
					<div className='text-red text-base'>{formik.errors.email}</div>
				)}
			</div>
			<div className='grid grid-cols-1 pt-4'>
				<label htmlFor='password' className='text-white text-base'>
					Password <span className='text-red text-base'>*</span>
				</label>
				<input
					name='password'
					type='password'
					className='w-full p-1 rounded text-sm'
					placeholder='********'
					onChange={formik.handleChange}
					value={formik.values.password}
				/>
				{formik.touched.password && formik.errors.password && (
					<div className='text-red text-base'>{formik.errors.password}</div>
				)}
			</div>
			<div className='grid grid-cols-1 pt-4 mb-8'>
				<label htmlFor='checkbox' className='text-white text-base'>
					<input
						type='checkbox'
						name='checkbox'
						className='bg-white rounded mr-2'
						onChange={formik.handleChange}
						checked={formik.values.checkbox}
					/>
					I agree to the
					<Link
						to='/terms-and-conditions'
						target='_blank'
						rel='noopener noreferrer'
						className='text-purple ml-2'
					>
						Terms of Service and Privacy Policy
					</Link>
					<span className='text-red text-base'>*</span>
				</label>
				{formik.touched.checkbox && formik.errors.checkbox && (
					<div className='text-red text-base'>{formik.errors.checkbox}</div>
				)}
			</div>
			<div className='flex justify-center'>
				<button
					type='submit'
					className='w-32 h-12 text-base bg-purple text-white rounded'
				>
					Sign Up
				</button>
			</div>
			<p className='text-white text-base text-center mt-6'>
				Already have an account?{' '}
				<Link to='/sign-in' className='text-purple mr-2'>
					Sign In
				</Link>
			</p>
		</form>
	);
}

export default SignUpForm;
